<script lang="ts" setup>
// ================================
// IMPORTS
// ================================
import { ref, computed, watch, toRef } from "vue"
import { useI18n } from "vue-i18n"
import moment from "moment-timezone"
import type { EnergyManagerIotAnalyse } from "@types"
import {
    useCreateIotAnalyse,
    type CreateIotAnalyseResult,
} from "@modules/energy-manager/composables"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Lancer une nouvelle analyse",
            start_date: "Début de période",
            end_date: "Fin de période",
            invalid_period:
                "Veuillez sélectionner deux dates valides (début de période < fin de période).",
            invalid_min_period:
                "La période d'analyse doit être d'au moins 2 mois.",
            temp_utilisation: {
                label: "Consigne en période de présence",
                hint: "Température du bâtiment lorsque celui-ci est occupé",
            },
            temp_no_utilisation: {
                label: "Consigne en période d'absence",
                hint: "Température du bâtiment lorsque celui-ci est inoccupé",
            },
            cancel: "Annuler",
            create: "Créer",
        },
    },
})

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        /**
         * Identifiant du bâtiment pour lequel créer l'analyse
         */
        buildingId?: string
        /**
         * Valeurs initiales pour préremplir le formulaire (ex. dernière analyse)
         */
        initialValues?: EnergyManagerIotAnalyse["inputs"] | null
    }>(),
    {
        buildingId: "",
        initialValues: null,
    }
)

const emit = defineEmits<{
    "update:visible": [value: boolean]
    cancel: []
    complete: [result: CreateIotAnalyseResult]
    refetch: []
}>()

const visible = defineModel<boolean>("visible", { default: false })

// ================================
// COMPOSABLES
// ================================
const buildingIdRef = toRef(props, "buildingId")
const {
    fetch: createAnalyse,
    isFetching,
    isSuccess,
    error,
} = useCreateIotAnalyse(buildingIdRef, () => emit("refetch"))

// ================================
// ÉTAT
// ================================
/**
 * Données du formulaire
 */
const formData = ref({
    startDate: null as Date | null,
    endDate: null as Date | null,
    tempUtilisation: 19,
    tempNoUtilisation: 17,
})

// ================================
// DONNÉES CALCULÉES
// ================================
const getIsValidDates = computed(() => {
    if (!formData.value.startDate || !formData.value.endDate) return false
    return formData.value.startDate < formData.value.endDate
})

const getIsMinPeriodValid = computed(() => {
    if (!formData.value.startDate || !formData.value.endDate) return true
    const diffMonths = moment(formData.value.endDate).diff(
        moment(formData.value.startDate),
        "months"
    )
    return diffMonths >= 2
})

const getIsFormValid = computed(() => {
    return (
        getIsValidDates.value &&
        getIsMinPeriodValid.value &&
        formData.value.tempUtilisation != null &&
        formData.value.tempNoUtilisation != null
    )
})

const getDateFieldError = computed(() => {
    if (!getIsValidDates.value) {
        return t("invalid_period")
    }
    if (!getIsMinPeriodValid.value) {
        return t("invalid_min_period")
    }
    return ""
})

// ================================
// LOGIQUE MÉTIER
// ================================
function initializeDefaultDates() {
    const today = moment().tz("Europe/Paris").toDate()
    const threeMonthsAgo = moment()
        .tz("Europe/Paris")
        .subtract(3, "months")
        .toDate()
    formData.value.startDate = threeMonthsAgo
    formData.value.endDate = today
    formData.value.tempUtilisation = 19
    formData.value.tempNoUtilisation = 17
}

function initializeFromValues(inputs: EnergyManagerIotAnalyse["inputs"]) {
    formData.value.startDate = inputs.date_from
        ? moment.tz(inputs.date_from, "YYYY-MM-DD", "Europe/Paris").toDate()
        : null
    formData.value.endDate = inputs.date_to
        ? moment.tz(inputs.date_to, "YYYY-MM-DD", "Europe/Paris").toDate()
        : null
    formData.value.tempUtilisation = inputs.temperature_utilisation ?? 19
    formData.value.tempNoUtilisation = inputs.temperature_nonutilisation ?? 17
}

watch(
    () => visible.value,
    (isVisible) => {
        if (isVisible) {
            if (props.initialValues) {
                initializeFromValues(props.initialValues)
            } else {
                initializeDefaultDates()
            }
        }
    }
)

// ================================
// INTERACTIONS
// ================================
async function onSubmit() {
    if (!getIsFormValid.value) return
    const startDateStr = moment(formData.value.startDate)
        .tz("Europe/Paris")
        .format("YYYY-MM-DD")
    const endDateStr = moment(formData.value.endDate)
        .tz("Europe/Paris")
        .format("YYYY-MM-DD")
    await createAnalyse({
        dateFrom: startDateStr,
        dateTo: endDateStr,
        tempUtilisation: formData.value.tempUtilisation,
        tempNoUtilisation: formData.value.tempNoUtilisation,
    })
    const result: CreateIotAnalyseResult = isSuccess.value
        ? { status: "success" }
        : {
              status: "error",
              error:
                  error.value?.message ??
                  "Erreur lors de la création de l'analyse",
          }
    if (result.status === "success") {
        visible.value = false
        emit("complete", result)
    }
}

function onCancel() {
    visible.value = false
    emit("cancel")
}
</script>

<template>
    <BaseDialog
        :visible="visible"
        modal
        :closable="false"
        dismissable-mask
        :header="t('title')"
        class="w-full max-w-2xl"
        :pt="{
            footer: 'flex justify-end gap-2',
        }"
        @update:visible="visible = $event"
    >
        <form @submit.prevent class="flex flex-col gap-6 pt-6">
            <fieldset class="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                <BaseInputWrapper
                    input-id="start-date"
                    :label="t('start_date')"
                    :error="getDateFieldError"
                >
                    <BaseDatePicker
                        id="start-date"
                        v-model="formData.startDate"
                        date-format="dd/mm/yy"
                        class="w-full"
                        :max-date="
                            moment().tz('Europe/Paris').add(1, 'day').toDate()
                        "
                        :invalid="!getIsValidDates || !getIsMinPeriodValid"
                    />
                </BaseInputWrapper>
                <BaseInputWrapper
                    input-id="end-date"
                    :label="t('end_date')"
                    :error="getDateFieldError"
                >
                    <BaseDatePicker
                        id="end-date"
                        v-model="formData.endDate"
                        date-format="dd/mm/yy"
                        class="w-full"
                        :max-date="
                            moment().tz('Europe/Paris').add(1, 'day').toDate()
                        "
                        :min-date="formData.startDate ?? undefined"
                        :invalid="!getIsValidDates || !getIsMinPeriodValid"
                    />
                </BaseInputWrapper>
                <BaseMessage
                    v-if="!getIsValidDates"
                    severity="error"
                    class="col-span-1 md:col-span-2 text-center"
                >
                    {{ t("invalid_period") }}
                </BaseMessage>
                <BaseMessage
                    v-if="!getIsMinPeriodValid"
                    severity="error"
                    class="col-span-1 md:col-span-2 text-center"
                >
                    {{ t("invalid_min_period") }}
                </BaseMessage>
            </fieldset>

            <fieldset class="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                <BaseInputWrapper
                    input-id="temp-utilisation"
                    :label="t('temp_utilisation.label')"
                    :hint="t('temp_utilisation.hint')"
                    suffix="°C"
                >
                    <BaseInputNumber
                        id="temp-utilisation"
                        v-model="formData.tempUtilisation"
                        :step="0.1"
                    />
                </BaseInputWrapper>
                <BaseInputWrapper
                    input-id="temp-no-utilisation"
                    :label="t('temp_no_utilisation.label')"
                    :hint="t('temp_no_utilisation.hint')"
                    suffix="°C"
                >
                    <BaseInputNumber
                        id="temp-no-utilisation"
                        v-model="formData.tempNoUtilisation"
                        :step="0.1"
                    />
                </BaseInputWrapper>
            </fieldset>

            <BaseMessage v-if="error" severity="error">
                {{ error?.message ?? error }}
            </BaseMessage>
        </form>

        <template #footer>
            <BaseButton :label="t('cancel')" variant="text" @click="onCancel" />
            <BaseButton
                :label="t('create')"
                :loading="isFetching"
                :disabled="!getIsFormValid || isFetching"
                @click="onSubmit"
            />
        </template>
    </BaseDialog>
</template>
